import { Graphics, Rectangle, Ticker } from "pixi.js"
import Entity, { CollisionType } from "./Entity"
import Player from "./Player"
import Wall from "./Wall"
import { TILE_SIZE, SCALE_FACTOR, SHOW_BOUNDING_BOXES, SHOW_DEBUG_GRAPHICS, SHOW_EMPTY_TILES, SHOW_HIT_BOXES, SHOW_SPRITE_BOUNDS } from "./config"
import Bomb from "./Bomb"
import PowerUp, { PowerUpEnum } from "./PowerUp"
import PowerUpCollection from "./PowerUpCollection"
import { Position } from "nipplejs"
import Poison, { PoisonEnum } from "./Poison"
import Clock, { ClockCallbackObject } from "./Clock"
import OverlayText from "./OverlayText"
import StartBlock from "./StartBlock"
import { PlayerIdBool, PlayerId } from "$types"
import FadingContainer from "./FadingContainer"

export type GameOverCallback = (winner: Player | null) => void

export default class Level extends FadingContainer {
  static LEVEL_TILE_SIZE = TILE_SIZE * SCALE_FACTOR
  static CLOSING_SPIRALS = 2

  static getTileCoords(position: { x: number, y: number }) {
    return {
      x: Math.round(position.x / Level.LEVEL_TILE_SIZE) * Level.LEVEL_TILE_SIZE,
      y: Math.round(position.y / Level.LEVEL_TILE_SIZE) * Level.LEVEL_TILE_SIZE
    }
  }

  protected _closingIterations = 0
  private _closingInterval: ClockCallbackObject | null = null

  private _levelName: string
  get levelName() {
    return this._levelName
  }

  private _maxWallCount: number = 0
  get maxWallCount() {
    return this._maxWallCount
  }

  private _levelWidth: number
  get levelWidth() {
    return this._levelWidth
  }

  private _levelHeight: number
  get levelHeight() {
    return this._levelHeight
  }

  private _levelTileWidth: number
  get levelTileWidth() {
    return this._levelTileWidth
  }

  private _levelTileHeight: number
  get levelTileHeight() {
    return this._levelTileHeight
  }

  players: {
    1: Player | null
    2: Player | null
    3: Player | null
    4: Player | null
  } = {
      "1": null,
      "2": null,
      "3": null,
      "4": null
    }

  private _startingPlayerPositions: { [key: number]: { x: number, y: number } } = {}

  graphics: Graphics = new Graphics()

  constructor(params: {
    x: number,
    y: number,
    levelData: string,
    activePlayers: PlayerIdBool,
    connectedPlayers: PlayerIdBool
  }) {
    super({
      x: params.x,
      y: params.y
    })

    const levelDataRows = params.levelData.split("\n")
    this._levelName = levelDataRows.shift()!

    const powerUpCollection = new PowerUpCollection()
    let poisonCount = 0
    for (let i = 0; i < levelDataRows.length; i++) {
      const row = levelDataRows.shift()!
      if (row === "") {
        break
      }

      const [key, value]: string[] = row.split("=")

      switch (key) {
        case "-": {
          this._maxWallCount = Number.parseInt(value)
          break
        }
        case "K": {
          powerUpCollection.powerUpCount.powerUpKick = Number.parseInt(value)
          break
        }
        case "B": {
          powerUpCollection.powerUpCount.powerUpBomb = Number.parseInt(value)
          break
        }
        case "D": {
          poisonCount = Number.parseInt(value)
          break
        }
        case "P": {
          powerUpCollection.powerUpCount.powerUpPunch = Number.parseInt(value)
          break
        }
        case "S": {
          powerUpCollection.powerUpCount.powerUpSkates = Number.parseInt(value)
          break
        }
        case "F": {
          powerUpCollection.powerUpCount.powerUpFire = Number.parseInt(value)
          break
        }
      }
    }

    const possibleWallPositions: { x: number, y: number }[] = []

    this._levelTileWidth = levelDataRows[0].length
    this._levelTileHeight = levelDataRows.length
    this._levelWidth = this._levelTileWidth * Level.LEVEL_TILE_SIZE
    this._levelHeight = this._levelTileHeight * Level.LEVEL_TILE_SIZE

    const background = new Graphics()
      .rect(0, 0, this._levelWidth, this._levelHeight)
      .fill(0x107830)
    this.addChild(background)

    levelDataRows.forEach((row, y) => {
      row.split("").forEach((cell, x) => {
        switch (cell) {
          case "1":
          case "2":
          case "3":
          case "4": {
            this._startingPlayerPositions[Number.parseInt(cell)] = { x: x * Level.LEVEL_TILE_SIZE, y: y * Level.LEVEL_TILE_SIZE }
            break
          }
          case "x": {
            this.addChild(new Wall({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              destroyable: false,
              content: null
            }))
            break
          }
          case "-": {
            possibleWallPositions.push({ x: x * Level.LEVEL_TILE_SIZE, y: y * Level.LEVEL_TILE_SIZE })
            break
          }
          case "b": {
            this.addChild(new Bomb({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              size: 2,
              timer: 1000
            }))
            break
          }
          case "B": {
            this.addChild(new PowerUp({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              type: PowerUpEnum.powerUpBomb
            }))
            break
          }
          case "S": {
            this.addChild(new PowerUp({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              type: PowerUpEnum.powerUpSkates
            }))
            break
          }
          case "K": {
            this.addChild(new PowerUp({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              type: PowerUpEnum.powerUpKick
            }))
            break
          }
          case "P": {
            this.addChild(new PowerUp({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              type: PowerUpEnum.powerUpPunch
            }))
            break
          }
          case "F": {
            this.addChild(new PowerUp({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              type: PowerUpEnum.powerUpFire
            }))
            break
          }
          case "D": {
            this.addChild(new Poison({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              type: PoisonEnum.slowed
            }))
            break
          }
          case "G": {
            this.addChild(new StartBlock({
              x: x * Level.LEVEL_TILE_SIZE,
              y: y * Level.LEVEL_TILE_SIZE,
              level: this,
              destroyable: false
            }))
          }
        }
      })
    })

    if (Object.keys(this._startingPlayerPositions).length !== 4) {
      throw new Error("Invalid level data. Must have 4 starting positions")
    }

    const wallContentList: (PowerUpEnum | PoisonEnum)[] = powerUpCollection.getPowerupTypesList()
    const poisonTypes = Object.values(PoisonEnum)
    for (let i = 0; i < poisonCount; i++) {
      wallContentList.push(poisonTypes[Math.floor(Math.random() * poisonTypes.length)])
    }

    for (let i = 0; i < this._maxWallCount; i++) {
      if (possibleWallPositions.length === 0) {
        break
      }

      const position = possibleWallPositions.splice(Math.floor(Math.random() * possibleWallPositions.length), 1)[0]

      const powerUpType: PowerUpEnum | PoisonEnum | null = wallContentList.length ? wallContentList.splice(Math.floor(Math.random() * wallContentList.length), 1)[0] : null
      this.addChild(new Wall({
        x: position.x,
        y: position.y,
        level: this,
        destroyable: true,
        content: powerUpType
      }))
    }

    if (SHOW_DEBUG_GRAPHICS) {
      this.addChild(this.graphics)
      this.graphics.zIndex = +Infinity
    }

    Object.entries(params.activePlayers).forEach(([playerId, active]) => {
      if (active) {
        this.addPlayer(Number(playerId) as PlayerId, params.connectedPlayers[Number(playerId) as PlayerId])
      }
    })
  }

  addPlayer(playerId: PlayerId, connected: boolean) {
    if (!this.players[playerId]) {
      const player = this.players[playerId] = new Player({
        level: this,
        playerId,
        ...this._startingPlayerPositions[playerId]
      })
      player.connected = connected
      this.addChild(player)
    }
  }

  checkCollisions(entity: Entity, type: CollisionType): Entity[] {
    return this.children.reduce<Entity[]>((acc, e) => {
      if (e instanceof Entity && e !== entity && entity.checkCollision(e, type)) {
        acc.push(e)
      }
      return acc
    }, [])
  }

  isTileBlocked(position: { x: number, y: number }, entity: Entity): boolean {
    return this.getEntitiesAtTileCoords(position).some(e => {
      if (e.isBlocking(entity.constructor)) {
        return true
      }
    })
  }

  getEntitiesInArea(rect: Rectangle, filter: Entity[] = []): Entity[] {
    return this.children.reduce<Entity[]>((acc, e) => {
      if (e instanceof Entity && !filter.includes(e)) {
        const { x, y, width, height } = e.getBoundingBox()
        if (rect.x < x + width && rect.x + rect.width > x && rect.y < y + height && rect.y + rect.height > y) {
          acc.push(e)
        }
      }
      return acc
    }, [])
  }

  getEntitiesAtTileCoords(positions: { x: number, y: number } | { x: number, y: number }[]): Entity[] {
    if (!Array.isArray(positions)) {
      positions = [positions]
    }
    return this.children.reduce<Entity[]>((acc, entity) => {
      if (entity instanceof Entity) {
        const { x: entityX, y: entityY } = entity.getTileCoords()
        for (const position of positions) {
          if (position.x === entityX && position.y === entityY) {
            acc.push(entity)
            break
          }
        }
      }
      return acc
    }, [])
  }

  getEmptyTilesCoordinates(): Position[] {
    const emptyTiles = new Set<string>()
    this.children.forEach(entity => {
      if (entity instanceof Entity) {
        const { x, y } = entity.getTileCoords()
        emptyTiles.add(`${x / Level.LEVEL_TILE_SIZE},${y / Level.LEVEL_TILE_SIZE}`)
      }
    })

    const emptyTilesArray: Position[] = []
    for (let y = 0; y < this._levelTileHeight; y++) {
      for (let x = 0; x < this._levelTileWidth; x++) {
        const key = `${x},${y}`
        if (!emptyTiles.has(key)) {
          emptyTilesArray.push({ x: x * Level.LEVEL_TILE_SIZE, y: y * Level.LEVEL_TILE_SIZE })
        }
      }
    }

    return emptyTilesArray
  }

  tickUpdate(ticker: Ticker) {
    if (SHOW_DEBUG_GRAPHICS) {
      this.graphics.clear()
    }

    this.children.forEach(child => {
      if (child instanceof Entity) {
        child.tickUpdate(ticker)
        if (SHOW_DEBUG_GRAPHICS) {
          if (SHOW_SPRITE_BOUNDS) {
            const bounds = child.getBounds()
            this.graphics.rect(bounds.x, bounds.y, bounds.width, bounds.height)
            this.graphics.stroke({ width: 2, color: 0xfeeb77 })

            const tileCoords = child.getTileCoords()
            this.graphics.circle(tileCoords.x, tileCoords.y, 5)
            this.graphics.fill(0xff0000)

            this.graphics.circle(child.x, child.y, 5)
            this.graphics.fill(0xffff00)
          }

          if (SHOW_BOUNDING_BOXES) {
            const boundingBox = child.getBoundingBox()
            this.graphics.rect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height)
            this.graphics.stroke({ width: 2, color: 0xff00ff })
          }

          if (SHOW_HIT_BOXES) {
            const hitBox = child.getHitBox()
            if (hitBox) {
              this.graphics.rect(hitBox.x, hitBox.y, hitBox.width, hitBox.height)
              this.graphics.stroke({ width: 2, color: 0xff3030 })
            }
          }
        }
      } else if (child instanceof OverlayText) {
        child.tickUpdate(ticker)
      }
    })

    if (SHOW_DEBUG_GRAPHICS && SHOW_EMPTY_TILES) {
      this.getEmptyTilesCoordinates().forEach(({ x, y }) => {
        this.graphics.rect(x, y, Level.LEVEL_TILE_SIZE, Level.LEVEL_TILE_SIZE)
        this.graphics.fill(0x00ff00)
      })
    }
  }

  private _closeTile(x: number, y: number): void {
    const tileCoords = Level.getTileCoords({ x, y })
    const entities = this.getEntitiesAtTileCoords({ x: tileCoords.x, y: tileCoords.y })
    const wall = new Wall({ x: tileCoords.x, y: tileCoords.y, level: this, destroyable: false, content: null, blink: true })
    this.addChild(wall)
    entities.forEach(entity => {
      if (wall.checkCollision(entity, CollisionType.BoundingBox)) {
        if (entity instanceof Player || entity instanceof Bomb) {
          entity.explode()
        } else {
          this.removeChild(entity)
        }
      }
    })
  }

  protected _closeLevelTiles(startingSpiral: number = 0, numberOfSpirals: number = 2): void {
    this._closingIterations++
    const tileSequence: { x: number, y: number }[] = []

    let left = 1, right = this._levelTileWidth - 2
    let top = 1, bottom = this._levelTileHeight - 2
    let currentSpiral = 0

    while (left <= right && top <= bottom) {
      if (currentSpiral >= startingSpiral && currentSpiral < startingSpiral + numberOfSpirals) {
        for (let i = bottom; i >= top; i--) tileSequence.push({ x: left, y: i })
        left++
        if (left > right) break

        for (let i = left; i <= right; i++) tileSequence.push({ x: i, y: top })
        top++
        if (top > bottom) break

        for (let i = top; i <= bottom; i++) tileSequence.push({ x: right, y: i })
        right--
        if (left > right) break

        for (let i = right; i >= left; i--) tileSequence.push({ x: i, y: bottom })
        bottom--
        if (top > bottom) break
      } else {
        left++
        top++
        right--
        bottom--
      }
      currentSpiral++
    }

    if (this._closingInterval) {
      Clock.clear(this._closingInterval)
    }

    this._closingInterval = Clock.setInterval(() => {
      const tile = tileSequence.shift()
      if (tile) {
        this._closeTile(tile.x * Level.LEVEL_TILE_SIZE, tile.y * Level.LEVEL_TILE_SIZE)
      }
    }, 500)
  }

  stopClosing() {
    if (this._closingInterval) {
      Clock.clear(this._closingInterval)
      this._closingInterval = null
    }
  }

  setPlayerConnected(playerId: PlayerId, connected: boolean) {
    if (!this.players[playerId]) return

    if (this.players[playerId]?.connected !== connected) {
      this.players[playerId]!.connected = connected
    }
  }

  getConnectedPlayers(): Player[] {
    return Object.values(this.players).reduce<Player[]>((acc, player) => {
      if (player && player.connected) {
        acc.push(player)
      }
      return acc
    }, [])
  }
}
